import { useEffect, useState, useMemo } from "react";
import { Plus, Edit, Trash2, X, Loader2, Save, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const PROMPT_CATEGORIES = [
  "Headshot",
  "Lifestyle",
  "Office & Workspace",
  "On Stage / Speaking",
  "Social Media",
  "Team & Client",
  "Website & Marketing",
  "Bonus: Professional Bio",
  "Bonus: Virtual Try-On",
  "Other",
];

const PLACEHOLDER_HINT = [
  "{{hair_description}}",
  "{{skin_tone}}",
  "{{wardrobe_notes}}",
  "{{preferred_style}}",
  "{{preferred_settings}}",
  "{{industry}}",
  "{{additional_notes}}",
];

const blank = {
  title: "",
  category: "Headshot",
  template_text: "",
  usage_tip: "",
  display_order: 100,
  is_active: true,
};

export default function AdminPrompts() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [filterCat, setFilterCat] = useState("All");
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/prompts");
      setItems(r.data.prompts || []);
    } catch {
      toast.error("Could not load prompts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const categories = useMemo(() => {
    const s = new Set(items.map((p) => p.category).filter(Boolean));
    return ["All", ...Array.from(s)];
  }, [items]);

  const filtered = useMemo(() => {
    return items.filter((p) => {
      if (filterCat !== "All" && p.category !== filterCat) return false;
      if (search && !`${p.title} ${p.template_text}`.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [items, filterCat, search]);

  const startNew = () => {
    setForm(blank);
    setEditing("new");
  };

  const startEdit = (it) => {
    setForm({
      title: it.title || "",
      category: it.category || "Headshot",
      template_text: it.template_text || "",
      usage_tip: it.usage_tip || "",
      display_order: it.display_order ?? 100,
      is_active: it.is_active !== false,
    });
    setEditing(it.id);
  };

  const close = () => {
    setEditing(null);
    setForm(blank);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    if (!form.template_text.trim()) {
      toast.error("Prompt text is required");
      return;
    }
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/admin/prompts", form);
        toast.success("Prompt created");
      } else {
        await api.put(`/admin/prompts/${editing}`, form);
        toast.success("Prompt updated");
      }
      close();
      load();
    } catch {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (it) => {
    try {
      await api.put(`/admin/prompts/${it.id}`, {
        title: it.title,
        category: it.category || "Other",
        template_text: it.template_text || "",
        usage_tip: it.usage_tip || "",
        display_order: it.display_order ?? 100,
        is_active: !it.is_active,
      });
      load();
    } catch {
      toast.error("Could not update");
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`Delete prompt "${it.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/prompts/${it.id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Could not delete");
    }
  };

  const insertPlaceholder = (ph) => {
    setForm((f) => ({ ...f, template_text: `${f.template_text || ""}${ph}` }));
  };

  return (
    <div data-testid="admin-prompts">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <span className="eyebrow">Prompts</span>
          <h1 className="font-display text-3xl md:text-4xl text-[#1A1F3D] mt-2">Prompt library</h1>
          <p className="text-sm text-[#52525b] mt-1">Edit, add, and reorder the prompts members see in Step 3.</p>
        </div>
        <button onClick={startNew} className="btn-primary" data-testid="add-prompt">
          <Plus className="w-4 h-4" /> Add new prompt
        </button>
      </div>

      <div className="mt-5 flex flex-wrap gap-3 items-center">
        <input
          className="bsp-input max-w-xs"
          placeholder="Search title or text…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="prompt-search"
        />
        <select
          className="bsp-input max-w-[220px]"
          value={filterCat}
          onChange={(e) => setFilterCat(e.target.value)}
          data-testid="prompt-filter-cat"
        >
          {categories.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <span className="text-xs text-[#52525b] ml-auto">{filtered.length} of {items.length}</span>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-7 h-7 text-[#D4A853] animate-spin" /></div>
      ) : (
        <div className="mt-6 bsp-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#FAFAF8] text-[#52525b] text-left text-xs uppercase tracking-wider">
                <tr>
                  <th className="p-3 font-semibold">Title</th>
                  <th className="p-3 font-semibold">Category</th>
                  <th className="p-3 font-semibold">Preview</th>
                  <th className="p-3 font-semibold text-right">Order</th>
                  <th className="p-3 font-semibold">Active</th>
                  <th className="p-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((it) => (
                  <tr key={it.id} className="border-t border-[#e5e7eb] hover:bg-[#FAFAF8] align-top" data-testid={`prompt-row-${it.id}`}>
                    <td className="p-3 font-medium text-[#1A1F3D] max-w-[220px]">{it.title}</td>
                    <td className="p-3 text-[#52525b] whitespace-nowrap">{it.category}</td>
                    <td className="p-3 text-[#52525b] max-w-[420px]">
                      <span className="line-clamp-2 block">{it.template_text}</span>
                    </td>
                    <td className="p-3 text-right">{it.display_order}</td>
                    <td className="p-3">
                      <button
                        onClick={() => toggleActive(it)}
                        className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${it.is_active ? "bg-[#D4A853]/20 text-[#1A1F3D]" : "bg-[#F5E6E0] text-[#52525b]"}`}
                        data-testid={`prompt-active-${it.id}`}
                      >
                        {it.is_active ? "Active" : "Inactive"}
                      </button>
                    </td>
                    <td className="p-3 text-right">
                      <div className="inline-flex gap-1.5">
                        <button onClick={() => startEdit(it)} className="w-9 h-9 rounded-md hover:bg-[#F5E6E0] text-[#1A1F3D] inline-flex items-center justify-center" data-testid={`edit-prompt-${it.id}`}>
                          <Edit className="w-4 h-4" />
                        </button>
                        <button onClick={() => remove(it)} className="w-9 h-9 rounded-md hover:bg-red-50 text-red-600 inline-flex items-center justify-center" data-testid={`del-prompt-${it.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={6} className="p-6 text-center text-[#52525b]">No prompts match.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={close}>
          <form onSubmit={save} className="bg-white rounded-2xl p-7 w-full max-w-3xl relative max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="prompt-form">
            <button type="button" onClick={close} className="absolute top-4 right-4 w-9 h-9 rounded-full bg-[#FAFAF8] flex items-center justify-center">
              <X className="w-5 h-5" />
            </button>
            <span className="eyebrow">{editing === "new" ? "New prompt" : "Edit prompt"}</span>
            <h2 className="font-display text-2xl text-[#1A1F3D] mt-1 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#D4A853]" />
              {editing === "new" ? "Add a new prompt" : "Update prompt"}
            </h2>
            <div className="mt-5 space-y-4">
              <div>
                <label className="text-sm font-medium text-[#1A1F3D]">Title</label>
                <input className="bsp-input mt-1" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="p-title" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-[#1A1F3D]">Category</label>
                  <select className="bsp-input mt-1" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="p-cat">
                    {PROMPT_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-[#1A1F3D]">Display order</label>
                  <input type="number" className="bsp-input mt-1" value={form.display_order} onChange={(e) => setForm({ ...form, display_order: parseInt(e.target.value || "0", 10) })} data-testid="p-order" />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-[#1A1F3D]">Prompt text</label>
                <textarea
                  className="bsp-input mt-1 font-mono text-[13px] leading-relaxed"
                  rows={10}
                  required
                  value={form.template_text}
                  onChange={(e) => setForm({ ...form, template_text: e.target.value })}
                  data-testid="p-text"
                />
                <div className="mt-2 flex flex-wrap gap-1.5">
                  <span className="text-xs text-[#52525b] mr-1">Insert placeholder:</span>
                  {PLACEHOLDER_HINT.map((ph) => (
                    <button
                      type="button"
                      key={ph}
                      onClick={() => insertPlaceholder(ph)}
                      className="text-[11px] px-2 py-0.5 rounded-full bg-[#F5E6E0] text-[#1A1F3D] hover:bg-[#D4A853]/30 transition"
                      data-testid={`p-ph-${ph}`}
                    >
                      {ph}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-[#1A1F3D]">Usage tip (optional)</label>
                <textarea
                  className="bsp-input mt-1"
                  rows={2}
                  placeholder="e.g. How to use: Copy this prompt, then in ChatGPT attach TWO images…"
                  value={form.usage_tip}
                  onChange={(e) => setForm({ ...form, usage_tip: e.target.value })}
                  data-testid="p-tip"
                />
                <p className="text-xs text-[#52525b] mt-1">Shown in a gold callout below the prompt text on the member page.</p>
              </div>
              <div className="flex items-center">
                <label className="inline-flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="accent-[#D4A853]" data-testid="p-active" />
                  Active (visible to members)
                </label>
              </div>
            </div>
            <div className="mt-7 flex justify-end gap-2">
              <button type="button" onClick={close} className="btn-ghost">Cancel</button>
              <button type="submit" disabled={saving} className="btn-primary" data-testid="p-save">
                <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save prompt"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
